// SPEC-009 §4.4 — single EventSource at the layout level fans the three
// backend SSE events out to the rest of the app via the tanstack-query
// cache + toast notifications.
//
// EventSource auto-reconnects on connection drop and respects the
// `credentials: 'include'` flag we set with withCredentials so the
// httpOnly admin cookie travels with the upgrade. The 14-min hard close
// the backend imposes (ADR-007) results in a normal EOF that browsers
// reconnect from cleanly.

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useI18n } from "@faceheart/i18n";
import type { Alert } from "@faceheart/api-types";
import { useAuth } from "../lib/auth/AuthProvider";
import { formatAlertMessage } from "../lib/alerts/format";
import { tryRefresh } from "../lib/api/client";

interface AlertCreatedPayload {
  id: string;
  severity: "critical" | "warning" | "info";
  type: string;
  message: string;
  message_key?: string;
  message_args?: Record<string, unknown>;
  metric: string;
  metric_value: number;
}

interface MeasurementCreatedPayload {
  measurement_id: string;
  employee_id: string;
  measured_at: string;
  alerts_count: number;
}

export function useEventStream() {
  const qc = useQueryClient();
  const { status } = useAuth();
  const { t } = useI18n("admin");

  useEffect(() => {
    if (status !== "authed") return;

    // We drive reconnection ourselves instead of relying on EventSource's
    // native auto-reconnect. Native reconnect just replays the existing cookie;
    // once the 15-min access token expires the SSE request 401s and the browser
    // retries the SAME expired cookie every ~3s forever (the "sse: connection
    // error" loop). Here, on every drop we first run the shared single-flight
    // token refresh — so an expired access token is silently renewed — then
    // reconnect with exponential backoff (reset to 1s on a healthy open).
    let es: EventSource | null = null;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let backoff = 1_000;
    let recovering = false;

    const wire = (src: EventSource) => {
      src.onopen = () => {
        // Healthy connection → reset the backoff window.
        backoff = 1_000;
      };

      src.addEventListener("alert.created", (ev) => {
        const e = ev as MessageEvent<string>;
        const a = safeParse<AlertCreatedPayload>(e.data);
        if (!a) return;

        const fn = a.severity === "critical" ? toast.error : toast.warning;
        fn(t("events.alertCreated"), {
          description: formatAlertMessage(t, a),
          duration: a.severity === "critical" ? 10_000 : 5_000,
        });

        qc.invalidateQueries({ queryKey: ["alerts"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
      });

      src.addEventListener("alert.acknowledged", (ev) => {
        const e = ev as MessageEvent<string>;
        const a = safeParse<Alert>(e.data);
        if (!a) return;

        // Optimistically merge into the cached alerts list; tanstack-query
        // refetches in the background so this is just for instant UX.
        qc.setQueriesData<{ items: Alert[] }>(
          { queryKey: ["alerts", "list"] },
          (old) =>
            old
              ? {
                  ...old,
                  items: old.items.map((x) => (x.id === a.id ? a : x)),
                }
              : old,
        );
        qc.invalidateQueries({ queryKey: ["dashboard"] });
      });

      // Bulk acknowledge emits ONE event (not N) to avoid overflowing the SSE
      // subscriber buffer on large batches. Just invalidate — the alerts list and
      // dashboard refetch in the background.
      src.addEventListener("alerts.acknowledged", () => {
        qc.invalidateQueries({ queryKey: ["alerts"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
      });

      src.addEventListener("measurement.created", (ev) => {
        const e = ev as MessageEvent<string>;
        const m = safeParse<MeasurementCreatedPayload>(e.data);
        if (!m) return;

        qc.invalidateQueries({ queryKey: ["dashboard"] });
        qc.invalidateQueries({
          queryKey: ["employee", m.employee_id, "measurements"],
        });
        qc.invalidateQueries({
          queryKey: ["employee", m.employee_id, "baselines"],
        });
      });

      src.onerror = () => {
        if (cancelled || recovering) return;
        recovering = true;
        // Stop native auto-reconnect; we reconnect manually after a refresh.
        src.close();
        void recover();
      };
    };

    const recover = async () => {
      // Most drops here are the access token expiring (EventSource can't refresh
      // itself) or the backend's ~14-min hard close. Mint a fresh cookie before
      // reconnecting so the new connection authenticates. tryRefresh is the same
      // single-flight used by the api client, so a parallel API 401 won't double-
      // refresh. We do NOT force a logout on failure (a transient network blip
      // also fails refresh) — we just back off and retry; a genuinely dead
      // session is surfaced by the next api() call the user triggers.
      await tryRefresh().catch(() => false);
      if (cancelled) return;
      retryTimer = setTimeout(() => {
        recovering = false;
        connect();
      }, backoff);
      backoff = Math.min(backoff * 2, 30_000);
    };

    const connect = () => {
      if (cancelled) return;
      es = new EventSource("/v1/events", { withCredentials: true });
      wire(es);
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      es?.close();
    };
  }, [status, qc, t]);
}

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}
